import { getActivePropertyId } from './auth';
import type {
  AssetEntityType,
  AssetWithTags,
  ChecklistRun,
  ChecklistRunItem,
  ChecklistRunItemStatus,
  ChecklistRunStatus,
  ChecklistRunWithItems,
  ChecklistTemplate,
  ChecklistTemplateWithItems,
  Domain,
  DomainDetailResponse,
  Consumable,
  Equipment,
  EquipmentDetail,
  EquipmentPart,
  EquipmentStatus,
  Kit,
  KitItemRow,
  KitItemType,
  KitWithItems,
  Project,
  ProjectPriority,
  ProjectStatus,
  Reference,
  Tool,
  ToolStatus,
  Vendor,
  Guide,
  LogEntityType,
  MaintenanceLog,
  PropertyZone,
} from './types';

/**
 * Fetch wrapper for the multitenant client:
 *  1. Sends the session cookie via `credentials: 'include'`.
 *  2. Injects `X-Property-Id` from localStorage when set so
 *     property-scoped routes know what to filter by.
 *  3. Surfaces the API's `{ error, details? }` envelope as a thrown
 *     Error whose message is human-readable.
 *  4. On 401, redirects to `/auth/sign-in` (the cookie is cleared
 *     server-side; we just need to land the user on the gate).
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const propertyId = getActivePropertyId();
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (propertyId) headers['x-property-id'] = propertyId;
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers,
  });

  if (res.status === 401) {
    if (typeof window !== 'undefined') {
      const here = window.location.pathname;
      // Don't redirect if we're already on an auth page (avoids
      // bouncing during sign-in / register).
      if (!here.startsWith('/auth/')) {
        window.location.assign(`/auth/sign-in?redirect=${encodeURIComponent(here)}`);
      }
    }
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string; details?: Record<string, string> };
      if (body.error) message = body.error;
      if (body.details && Object.keys(body.details).length > 0) {
        const fields = Object.entries(body.details)
          .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
          .join('; ');
        message = `${message} (${fields})`;
      }
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  return res.json() as Promise<T>;
}

export interface HealthResponse {
  ok: boolean;
  ts: number;
}

export function fetchHealth(): Promise<HealthResponse> {
  return request<HealthResponse>('/api/health');
}

// ── Auth surface ──────────────────────────────────────────────

export type Role = 'read' | 'runner' | 'owner' | 'admin';

export interface AuthAccount {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuthUser {
  id: string;
  email: string;
  fullName: string | null;
  status: string;
  emailVerifiedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuthProperty {
  id: string;
  accountId: string;
  name: string;
  slug: string;
  description: string | null;
  address: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface MeResponse {
  user: AuthUser;
  accounts: AuthAccount[];
  activeAccount: AuthAccount;
  properties: AuthProperty[];
  role: Role;
}

export interface RegisterBody {
  email: string;
  password: string;
  fullName: string;
  accountName: string;
  propertyName?: string;
}

export interface RegisterResponse {
  user: AuthUser;
  account: AuthAccount;
  property: AuthProperty;
}

export function fetchMe(): Promise<MeResponse> {
  return request<MeResponse>('/api/auth/me');
}

export function login(email: string, password: string): Promise<{ user: AuthUser; account: AuthAccount }> {
  return request<{ user: AuthUser; account: AuthAccount }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export function register(body: RegisterBody): Promise<RegisterResponse> {
  return request<RegisterResponse>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function logout(): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/auth/logout', { method: 'POST' });
}

export function switchAccount(accountId: string): Promise<{ account: AuthAccount }> {
  return request<{ account: AuthAccount }>('/api/auth/switch-account', {
    method: 'POST',
    body: JSON.stringify({ accountId }),
  });
}

export function leaveAccount(): Promise<{ account: AuthAccount }> {
  return request<{ account: AuthAccount }>('/api/auth/leave-account', {
    method: 'POST',
  });
}

export function forgotPassword(email: string): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function resetPassword(token: string, password: string): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

export function acceptInvite(
  token: string,
  body: { password?: string; fullName?: string },
): Promise<{ user: AuthUser; account: AuthAccount }> {
  return request<{ user: AuthUser; account: AuthAccount }>('/api/auth/accept-invite', {
    method: 'POST',
    body: JSON.stringify({ token, ...body }),
  });
}

// ── Properties + Users (admin) ───────────────────────────────

export interface CreatePropertyBody {
  name: string;
  slug?: string;
  description?: string | null;
  address?: string | null;
}

export function fetchProperties(): Promise<AuthProperty[]> {
  return request<AuthProperty[]>('/api/properties');
}

export function createProperty(body: CreatePropertyBody): Promise<AuthProperty> {
  return request<AuthProperty>('/api/properties', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function patchPropertyApi(
  id: string,
  body: Partial<CreatePropertyBody> & { status?: 'active' | 'archived' },
): Promise<AuthProperty> {
  return request<AuthProperty>(`/api/properties/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deletePropertyApi(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/properties/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export interface MemberRow {
  user: AuthUser;
  role: Role;
  joinedAt: string;
  propertyAccess: string[];
}

export interface InviteRow {
  id: string;
  email: string;
  role: Role;
  propertyIds: string | null;
  expiresAt: string;
  createdAt: string;
}

export function fetchMembers(): Promise<MemberRow[]> {
  return request<MemberRow[]>('/api/users');
}

export function fetchPendingInvites(): Promise<InviteRow[]> {
  return request<InviteRow[]>('/api/users/invites');
}

export interface InviteUserBody {
  email: string;
  role: Role;
  propertyIds?: string[];
}

export interface InviteIssued {
  invite: InviteRow;
  acceptUrl: string;
  mailDelivered: boolean;
  mailError?: string;
}

export function inviteUser(body: InviteUserBody): Promise<InviteIssued> {
  return request<InviteIssued>('/api/users/invite', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function revokeInvite(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/users/invite/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function patchMember(
  id: string,
  body: { role?: Role; propertyIds?: string[] | null },
): Promise<MemberRow> {
  return request<MemberRow>(`/api/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function removeMember(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/users/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function fetchDomains(): Promise<Domain[]> {
  return request<Domain[]>('/api/domains');
}

export function fetchDomain(slug: string): Promise<DomainDetailResponse> {
  return request<DomainDetailResponse>(`/api/domains/${encodeURIComponent(slug)}`);
}

export function fetchZones(): Promise<PropertyZone[]> {
  return request<PropertyZone[]>('/api/zones');
}

// ── Guides ───────────────────────────────────────────────────

export interface CreateGuideBody {
  slug: string;
  title: string;
  domainId: string;
  bodyMd: string;
  lastReviewed?: string | null;
}

export function fetchGuides(domain?: string): Promise<Guide[]> {
  const qs = domain ? `?domain=${encodeURIComponent(domain)}` : '';
  // Defensive parse: accept the documented `{ guides: [...] }`
  // envelope OR a bare array (older worker versions), so a bundle/
  // worker shape drift never lands the consumer with a non-iterable.
  return request<{ guides?: Guide[] } | Guide[]>(`/api/guides${qs}`).then((r) => {
    if (Array.isArray(r)) return r;
    if (r && Array.isArray((r as { guides?: Guide[] }).guides)) {
      return (r as { guides: Guide[] }).guides;
    }
    return [];
  });
}

export function fetchGuide(idOrSlug: string): Promise<Guide> {
  return request<{ guide: Guide }>(`/api/guides/${encodeURIComponent(idOrSlug)}`).then((r) => r.guide);
}

export function createGuide(body: CreateGuideBody): Promise<Guide> {
  return request<{ guide: Guide }>('/api/guides', {
    method: 'POST',
    body: JSON.stringify(body),
  }).then((r) => r.guide);
}

export function patchGuideApi(id: string, body: CreateGuideBody): Promise<Guide> {
  return request<{ guide: Guide }>(`/api/guides/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }).then((r) => r.guide);
}

export function deleteGuideApi(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/guides/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── Checklists ───────────────────────────────────────────────

export function fetchChecklistTemplates(domain?: string): Promise<ChecklistTemplate[]> {
  const qs = domain ? `?domain=${encodeURIComponent(domain)}` : '';
  return request<ChecklistTemplate[]>(`/api/checklist-templates${qs}`);
}

export function fetchChecklistTemplate(idOrSlug: string): Promise<ChecklistTemplateWithItems> {
  return request<ChecklistTemplateWithItems>(
    `/api/checklist-templates/${encodeURIComponent(idOrSlug)}`,
  );
}

export interface CreateTemplateBody {
  title: string;
  slug: string;
  domainId: string;
  description?: string | null;
  season?: 'spring' | 'summer' | 'fall' | 'winter' | 'any';
  recurrenceRule?: string | null;
  items: Array<{
    label: string;
    description?: string | null;
    required?: boolean;
  }>;
}

export function createChecklistTemplate(
  body: CreateTemplateBody,
): Promise<ChecklistTemplateWithItems> {
  return request<ChecklistTemplateWithItems>('/api/checklist-templates', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function patchChecklistTemplate(
  id: string,
  body: CreateTemplateBody,
): Promise<ChecklistTemplateWithItems> {
  return request<ChecklistTemplateWithItems>(
    `/api/checklist-templates/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export function deleteChecklistTemplate(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/api/checklist-templates/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

export interface ChecklistRunsFilter {
  status?: ChecklistRunStatus;
  year?: number;
  season?: string;
  limit?: number;
}

export function fetchChecklistRuns(filter: ChecklistRunsFilter = {}): Promise<ChecklistRun[]> {
  const params = new URLSearchParams();
  if (filter.status) params.set('status', filter.status);
  if (filter.year !== undefined) params.set('year', String(filter.year));
  if (filter.season) params.set('season', filter.season);
  if (filter.limit !== undefined) params.set('limit', String(filter.limit));
  const qs = params.toString();
  return request<ChecklistRun[]>(`/api/checklist-runs${qs ? `?${qs}` : ''}`);
}

export function fetchChecklistRun(id: string): Promise<ChecklistRunWithItems> {
  return request<ChecklistRunWithItems>(`/api/checklist-runs/${encodeURIComponent(id)}`);
}

export interface CreateRunBody {
  templateId: string;
  year?: number;
  season?: string;
  title?: string;
}

export function createChecklistRun(body: CreateRunBody): Promise<ChecklistRunWithItems> {
  return request<ChecklistRunWithItems>('/api/checklist-runs', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function patchChecklistRun(
  id: string,
  patch: { status?: ChecklistRunStatus; notes?: string | null; title?: string },
): Promise<ChecklistRunWithItems> {
  return request<ChecklistRunWithItems>(`/api/checklist-runs/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function deleteChecklistRun(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/checklist-runs/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function patchChecklistRunItem(
  id: string,
  patch: { status?: ChecklistRunItemStatus; notes?: string | null },
): Promise<{ item: ChecklistRunItem; run: ChecklistRun }> {
  return request<{ item: ChecklistRunItem; run: ChecklistRun }>(
    `/api/checklist-run-items/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
}

// ── Maintenance logs ─────────────────────────────────────────

export interface LogsFilter {
  domain?: string;
  zone?: string;
  entityType?: LogEntityType;
  entityId?: string;
  before?: string;
  limit?: number;
}

export function fetchLogs(filter: LogsFilter = {}): Promise<MaintenanceLog[]> {
  const params = new URLSearchParams();
  if (filter.domain) params.set('domain', filter.domain);
  if (filter.zone) params.set('zone', filter.zone);
  if (filter.entityType) params.set('entityType', filter.entityType);
  if (filter.entityId) params.set('entityId', filter.entityId);
  if (filter.before) params.set('before', filter.before);
  if (filter.limit !== undefined) params.set('limit', String(filter.limit));
  const qs = params.toString();
  return request<MaintenanceLog[]>(`/api/logs${qs ? `?${qs}` : ''}`);
}

export interface CreateLogBody {
  domainId: string;
  zoneId?: string | null;
  entityType?: LogEntityType | null;
  entityId?: string | null;
  performedAt: string;
  title: string;
  notes?: string | null;
  cost?: number | null;
  vendorId?: string | null;
}

export function createLog(body: CreateLogBody): Promise<MaintenanceLog> {
  return request<MaintenanceLog>('/api/logs', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fetchLog(id: string): Promise<MaintenanceLog> {
  return request<MaintenanceLog>(`/api/logs/${encodeURIComponent(id)}`);
}

export function patchLog(id: string, patch: Partial<CreateLogBody>): Promise<MaintenanceLog> {
  return request<MaintenanceLog>(`/api/logs/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function deleteLog(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/logs/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── Assets / photos ──────────────────────────────────────────

export interface AssetsFilter {
  domain?: string;
  zone?: string;
  entityType?: AssetEntityType;
  entityId?: string;
  tag?: string;
  before?: string;
  limit?: number;
}

export function fetchAssets(filter: AssetsFilter = {}): Promise<AssetWithTags[]> {
  const params = new URLSearchParams();
  if (filter.domain) params.set('domain', filter.domain);
  if (filter.zone) params.set('zone', filter.zone);
  if (filter.entityType) params.set('entityType', filter.entityType);
  if (filter.entityId) params.set('entityId', filter.entityId);
  if (filter.tag) params.set('tag', filter.tag);
  if (filter.before) params.set('before', filter.before);
  if (filter.limit !== undefined) params.set('limit', String(filter.limit));
  const qs = params.toString();
  return request<AssetWithTags[]>(`/api/assets${qs ? `?${qs}` : ''}`);
}

export interface UploadAssetMeta {
  domainId?: string | null;
  zoneId?: string | null;
  entityType?: AssetEntityType | null;
  entityId?: string | null;
  caption?: string | null;
  takenAt?: string | null;
  tags?: string[];
}

/**
 * Multipart asset upload. Bypasses the JSON `request<T>` wrapper
 * because we need to send form data, not JSON. Cookie auth rides
 * via `credentials: 'include'`; property scope via the header.
 */
export async function uploadAsset(file: File, meta: UploadAssetMeta): Promise<AssetWithTags> {
  const propertyId = getActivePropertyId();
  const fd = new FormData();
  fd.append('file', file);
  fd.append('meta', JSON.stringify(meta));
  const headers: Record<string, string> = {};
  if (propertyId) headers['x-property-id'] = propertyId;
  const res = await fetch('/api/assets', {
    method: 'POST',
    credentials: 'include',
    headers,
    body: fd,
  });
  if (res.status === 401) {
    if (typeof window !== 'undefined') {
      window.location.assign('/auth/sign-in');
    }
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json() as Promise<AssetWithTags>;
}

export function fetchAsset(id: string): Promise<AssetWithTags> {
  return request<AssetWithTags>(`/api/assets/${encodeURIComponent(id)}`);
}

export function patchAsset(
  id: string,
  patch: { caption?: string | null; takenAt?: string | null; tags?: string[] },
): Promise<AssetWithTags> {
  return request<AssetWithTags>(`/api/assets/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function deleteAsset(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/assets/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * Builds a `<img src="…">`-friendly URL for an asset's bytes. Same
 * origin as the app, so the session cookie travels automatically
 * with the image request — no query-param token needed.
 */
export function assetDownloadUrl(id: string): string {
  return `/api/assets/${encodeURIComponent(id)}/download`;
}

// ── Equipment ────────────────────────────────────────────────

export function fetchEquipmentList(): Promise<Equipment[]> {
  return request<Equipment[]>('/api/equipment');
}

export function fetchEquipment(id: string): Promise<EquipmentDetail> {
  return request<EquipmentDetail>(`/api/equipment/${encodeURIComponent(id)}`);
}

export interface CreateEquipmentBody {
  name: string;
  category: string;
  brand?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  purchaseDate?: string | null;
  storageZoneId?: string | null;
  notes?: string | null;
  status?: EquipmentStatus;
}

export function createEquipment(body: CreateEquipmentBody): Promise<EquipmentDetail> {
  return request<EquipmentDetail>('/api/equipment', { method: 'POST', body: JSON.stringify(body) });
}

export function patchEquipment(id: string, body: Partial<CreateEquipmentBody>): Promise<EquipmentDetail> {
  return request<EquipmentDetail>(`/api/equipment/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteEquipment(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/equipment/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export interface CreatePartBody {
  partName: string;
  partNumber?: string | null;
  notes?: string | null;
}

export function addEquipmentPart(equipmentId: string, body: CreatePartBody): Promise<EquipmentPart> {
  return request<EquipmentPart>(`/api/equipment/${encodeURIComponent(equipmentId)}/parts`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function patchEquipmentPart(
  equipmentId: string,
  partId: string,
  body: Partial<CreatePartBody>,
): Promise<EquipmentPart> {
  return request<EquipmentPart>(
    `/api/equipment/${encodeURIComponent(equipmentId)}/parts/${encodeURIComponent(partId)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export function deleteEquipmentPart(
  equipmentId: string,
  partId: string,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/api/equipment/${encodeURIComponent(equipmentId)}/parts/${encodeURIComponent(partId)}`,
    { method: 'DELETE' },
  );
}

// ── Tools ────────────────────────────────────────────────────

export interface CreateToolBody {
  name: string;
  category: string;
  brand?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  purchaseDate?: string | null;
  storageZoneId?: string | null;
  status?: ToolStatus;
  notes?: string | null;
}

export function fetchTools(): Promise<Tool[]> {
  return request<Tool[]>('/api/tools');
}
export function createTool(body: CreateToolBody): Promise<Tool> {
  return request<Tool>('/api/tools', { method: 'POST', body: JSON.stringify(body) });
}
export function patchTool(id: string, body: Partial<CreateToolBody>): Promise<Tool> {
  return request<Tool>(`/api/tools/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}
export function deleteTool(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/tools/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── Consumables ──────────────────────────────────────────────

export interface CreateConsumableBody {
  name: string;
  category: string;
  quantity?: number | null;
  unit?: string | null;
  minimumQuantity?: number | null;
  expirationDate?: string | null;
  storageZoneId?: string | null;
  notes?: string | null;
}

export function fetchConsumables(): Promise<Consumable[]> {
  return request<Consumable[]>('/api/consumables');
}
export function fetchLowStock(): Promise<Consumable[]> {
  return request<Consumable[]>('/api/consumables/low-stock');
}
export function createConsumable(body: CreateConsumableBody): Promise<Consumable> {
  return request<Consumable>('/api/consumables', { method: 'POST', body: JSON.stringify(body) });
}
export function patchConsumable(id: string, body: Partial<CreateConsumableBody>): Promise<Consumable> {
  return request<Consumable>(`/api/consumables/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}
export function deleteConsumable(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/consumables/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── Kits ─────────────────────────────────────────────────────

export interface CreateKitBody {
  name: string;
  slug: string;
  description?: string | null;
  domainId?: string | null;
}

export function fetchKits(): Promise<Kit[]> {
  return request<Kit[]>('/api/kits');
}
export function fetchKit(id: string): Promise<KitWithItems> {
  return request<KitWithItems>(`/api/kits/${encodeURIComponent(id)}`);
}
export function createKit(body: CreateKitBody): Promise<KitWithItems> {
  return request<KitWithItems>('/api/kits', { method: 'POST', body: JSON.stringify(body) });
}
export function deleteKit(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/kits/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export function addKitItemApi(
  kitId: string,
  body: { itemType: KitItemType; itemId: string; quantity?: number | null; notes?: string | null },
): Promise<KitItemRow> {
  return request<KitItemRow>(`/api/kits/${encodeURIComponent(kitId)}/items`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
export function deleteKitItem(kitId: string, itemId: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/api/kits/${encodeURIComponent(kitId)}/items/${encodeURIComponent(itemId)}`,
    { method: 'DELETE' },
  );
}

// ── Projects ─────────────────────────────────────────────────

export interface ProjectsFilter {
  status?: ProjectStatus | ProjectStatus[];
  priority?: ProjectPriority;
  domain?: string;
  zone?: string;
}

export interface CreateProjectBody {
  domainId?: string | null;
  zoneId?: string | null;
  title: string;
  description?: string | null;
  status?: ProjectStatus;
  priority?: ProjectPriority;
}

export function fetchProjects(filter: ProjectsFilter = {}): Promise<Project[]> {
  const params = new URLSearchParams();
  if (filter.status) {
    params.set('status', Array.isArray(filter.status) ? filter.status.join(',') : filter.status);
  }
  if (filter.priority) params.set('priority', filter.priority);
  if (filter.domain) params.set('domain', filter.domain);
  if (filter.zone) params.set('zone', filter.zone);
  const qs = params.toString();
  return request<Project[]>(`/api/projects${qs ? `?${qs}` : ''}`);
}

export function fetchProject(id: string): Promise<Project> {
  return request<Project>(`/api/projects/${encodeURIComponent(id)}`);
}

export function createProject(body: CreateProjectBody): Promise<Project> {
  return request<Project>('/api/projects', { method: 'POST', body: JSON.stringify(body) });
}

export function patchProject(id: string, body: Partial<CreateProjectBody>): Promise<Project> {
  return request<Project>(`/api/projects/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteProject(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/projects/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── Vendors ──────────────────────────────────────────────────

export interface CreateVendorBody {
  name: string;
  trade?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  notes?: string | null;
  status?: 'active' | 'archived';
}

export function fetchVendors(): Promise<Vendor[]> {
  return request<Vendor[]>('/api/vendors');
}
export function createVendor(body: CreateVendorBody): Promise<Vendor> {
  return request<Vendor>('/api/vendors', { method: 'POST', body: JSON.stringify(body) });
}
export function patchVendor(id: string, body: Partial<CreateVendorBody>): Promise<Vendor> {
  return request<Vendor>(`/api/vendors/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}
export function deleteVendor(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/vendors/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── References ───────────────────────────────────────────────

export interface CreateReferenceBody {
  domainId?: string | null;
  title: string;
  url?: string | null;
  notes?: string | null;
}

export function fetchReferences(domainId?: string): Promise<Reference[]> {
  const qs = domainId ? `?domain=${encodeURIComponent(domainId)}` : '';
  return request<Reference[]>(`/api/references${qs}`);
}
export function createReference(body: CreateReferenceBody): Promise<Reference> {
  return request<Reference>('/api/references', { method: 'POST', body: JSON.stringify(body) });
}
export function deleteReference(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/references/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
